import React, { useCallback } from "react";
import { Theme, useTheme, Popover, MenuItem, alpha } from "@mui/material";
import { useDispatch } from "react-redux";
import { openHistoryWindow, openWindow } from "src/redux/actions/Mosaic.Actions";
import { MosaicDirection } from "src/utils/MosaicTree";
import { openDrawer } from "src/redux/actions/App.Actions";
import { deletePairing, deletePlannedPairing } from "src/redux/actions/CrewSchedule/pairings";
import { setFocusedCrewSchedPairing } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { closeContextMenu } from "src/redux/actions/ContextMenu.Actions";
// API calls
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { debounce } from "lodash";
import { convertToTimelinePairing, IPairing } from "@vmosolutions/oc-interfaces";
import { openModal, closeModal } from "src/redux/actions/App.Actions";

/** Flight/Event chip context menu properties  */
interface IContextProps {
  /** The incoming pairing id */
  pairingId: string;
  /** Short Code identifier */
  shortCode: string;
  /** true when this chip represents a planned pairing */
  isPlanned?: boolean;
  /** Optional id of a parent context menu (e.g. CrewSchedule context menu) to close */
  contextMenuId?: string;
  /** Anchor Element used to anchor the context menu, null closes menu */
  anchorEl: HTMLDivElement;
  /** Used to close the menu by passing a null value back to parent */
  setAnchor: (anchor: any) => void;
}

const ShortCodeContextMenuStyles = (theme: Theme): any => ({
  boxShadow: theme.shadows[1],
  borderRadius: "3px",
  backgroundColor: alpha(theme.palette.common.black, 0.85),
  "& .pairing_popover_menu": {
    padding: 0,
    "& li": {
      fontSize: 12,
      "&:hover": { color: theme.palette.primary.contrastText },
    },
  },
});

/**
 * Component used to display a small context menu when right clicking on the pairing short code.
 * @param props - IContextProps
 */
export const ShortCodeContextMenu = (props: IContextProps): JSX.Element => {
  const { pairingId, shortCode, anchorEl, setAnchor, isPlanned, contextMenuId } = props;
  const theme: Theme = useTheme();
  const dispatch = useDispatch();

  const openSummary = useCallback(
    (event: React.MouseEvent): void => {
      event.preventDefault();
      event.stopPropagation(); //! Ensure right click doesn't select if used in table row.
      dispatch(openDrawer());
      dispatch(
        openWindow(
          `${shortCode} Summary`,
          "PairingSummaryWindow",
          [],
          { pairingId, crewId: null },
          "context",
          true,
          MosaicDirection.Right,
          940
        )
      );
    },
    [dispatch, pairingId, shortCode]
  );

  const editPairing = useCallback(
    (event: React.MouseEvent): void => {
      event.preventDefault();
      event.stopPropagation(); //! Ensure right click doesn't select if used in table row.
      dispatch(openDrawer());
      dispatch(openWindow("Edit Pairing", "PairingEditorWindow", [], { pairingId }, "context", true, MosaicDirection.Right, 940));
    },
    [dispatch, pairingId]
  );

  const openHistory = useCallback(async (): Promise<void> => {
    const notifications: INotifications = {
      dispatch: dispatch,
      infoMessage: "Loading Pairing History...",
      successMessage: "Pairing History Successfully Loaded",
      errorMessage: "Error: Unable to Load Pairing History",
    };
    const loadedPairing = await AmplifyAPIWrapper.Get<IPairing>(`/pairings/${pairingId}`, notifications);

    if (loadedPairing?._id) {
      dispatch(openDrawer());
      dispatch(
        openHistoryWindow(
          "Pairing History",
          {
            collection: "pairings",
            id: pairingId,
            pairing: convertToTimelinePairing(loadedPairing),
          },
          []
        )
      );
    }
  }, [dispatch, pairingId]);

  const handleDeletePairing = useCallback(
    (event: React.MouseEvent): void => {
      event.preventDefault();
      event.stopPropagation(); //! Ensure right click doesn't select if used in table row.
      //setActiveEventModal({ type: EventModalTypes.DeletePairing });
      if (pairingId) {
        setAnchor(null);
        if (contextMenuId) {
          dispatch(closeContextMenu(contextMenuId));
        }
        dispatch(
          openModal({
            root: {
              data: {
                title: isPlanned ? "Remove Planned Event" : "Delete Pairing",
                message: isPlanned
                  ? "Are you certain you wish to remove this planned event? This operation cannot be undone."
                  : "Are you certain you wish to delete this pairing? This operation cannot be undone.",
                buttons: [
                  {
                    label: "CANCEL",
                    handler: () => {
                      dispatch(closeModal({ root: { data: null } }));
                    },
                  },
                  {
                    label: isPlanned ? "REMOVE PLANNED EVENT" : "DELETE PAIRING",
                    handler: () => {
                      if (isPlanned) {
                        deletePlannedPairing(pairingId, dispatch);
                      } else {
                        dispatch(openDrawer());
                        deletePairing(pairingId, dispatch);
                      }
                      dispatch(closeModal({ root: { data: null } }));
                    },
                  },
                ],
              },
            },
          })
        );
      }
    },
    [pairingId, dispatch, isPlanned, setAnchor, contextMenuId]
  );

  /** If user has opened the context menu and moves outside of it, close it out. */
  // TODO Look into adding a delayed timer so that menu doesn't disappear so quickly on mouse out (with reset if they move back in)
  const handleCloseContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
      if (event) {
        setAnchor(null);
      }
    },
    [setAnchor]
  );

  const handleJumpToPairing = useCallback(
    (event: React.MouseEvent): void => {
      event.preventDefault();
      event.stopPropagation(); //! Ensure right click doesn't select if used in table row.
      dispatch(setFocusedCrewSchedPairing(pairingId));
    },
    [pairingId, dispatch]
  );

  return (
    <Popover
      id={`${pairingId}_shortcode_context_menu`}
      style={{ zIndex: 9999 }}
      open={!!anchorEl}
      onClose={() => setAnchor(null)}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      disableRestoreFocus
      slotProps={{
        paper: {
          sx: ShortCodeContextMenuStyles(theme),
        },
      }}
    >
      <div className="pairing_popover_menu" onMouseLeave={handleCloseContextMenu}>
        {!isPlanned && (
          <>
            <MenuItem onClick={handleJumpToPairing}>Jump to Pairing</MenuItem>
            <MenuItem onClick={openSummary}>Pairing Summary</MenuItem>
            <MenuItem onClick={editPairing}>Edit Pairing</MenuItem>
          </>
        )}
        <MenuItem onClick={debounce(openHistory, 1000)}>View Pairing History</MenuItem>
        <MenuItem onClick={handleDeletePairing}>{isPlanned ? "Remove Planned Event" : "Delete Pairing"}</MenuItem>
      </div>
    </Popover>
  );
};
